/**
 * Contratos compartidos entre la API y la SPA.
 *
 * Lo que vive aquí es lo que **ambos lados** necesitan entender igual: formas
 * de petición y respuesta, y las reglas de validación que se aplican en los
 * dos sitios.
 *
 * Por qué no vale con "validar en el servidor y ya": el servidor sigue siendo
 * la autoridad, pero si el formulario no conoce las mismas reglas, el usuario
 * se entera de que su zona horaria no vale después de pulsar Guardar. Compartir
 * el esquema evita que las dos validaciones se separen con el tiempo.
 *
 * Lo que **no** va aquí: nada que toque la base de datos, ni secretos, ni
 * lógica de servidor. Este paquete acaba dentro del bundle del navegador.
 */

import { z } from 'zod';

/* -------------------------------------------------------------------------
 * Roles
 * ---------------------------------------------------------------------- */

/**
 * Los cinco roles del §12.1.
 *
 * Se repiten aquí (y no se importan de `@digitalshare/core`) porque ese paquete
 * es de servidor y arrastraría dependencias al navegador. La prueba
 * `roles.test.ts` comprueba que las listas no se separen.
 */
export const orgRoleSchema = z.enum(['owner', 'admin', 'editor', 'author', 'viewer']);
export type OrgRole = z.infer<typeof orgRoleSchema>;

/* -------------------------------------------------------------------------
 * Ajustes de organización
 * ---------------------------------------------------------------------- */

/**
 * Zona horaria IANA.
 *
 * Se valida contra el propio motor de fechas del entorno en vez de contra una
 * lista fija: una lista se queda vieja cada vez que un país cambia su horario,
 * y eso pasa más a menudo de lo que parece.
 */
const timezoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(
    (value) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'No es una zona horaria IANA válida (por ejemplo: America/Santo_Domingo).' },
  );

export const orgSettingsSchema = z.object({
  orgId: z.string(),
  timezone: timezoneSchema,
  requireApproval: z.boolean(),
  maxPublicationsPerHour: z.number().int().min(1).max(10_000),
  paused: z.boolean(),
  pausedAt: z.iso.datetime().nullable(),
  pausedBy: z.string().nullable(),
});

export type OrgSettings = z.infer<typeof orgSettingsSchema>;

/** Lo que se puede cambiar. `paused` no está: se acciona con /pause y /resume. */
export const orgSettingsUpdateSchema = z
  .object({
    timezone: timezoneSchema,
    requireApproval: z.boolean(),
    maxPublicationsPerHour: z.number().int().min(1).max(10_000),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'No hay nada que actualizar.',
  });

export type OrgSettingsUpdate = z.infer<typeof orgSettingsUpdateSchema>;

/* -------------------------------------------------------------------------
 * Errores
 * ---------------------------------------------------------------------- */

/**
 * `application/problem+json` (RFC 9457), el formato de error de la API (§9).
 *
 * `passthrough` porque cada problema puede añadir campos propios; la SPA lee
 * los que conoce e ignora el resto sin romperse.
 */
export const problemSchema = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
  })
  .loose();

export type Problem = z.infer<typeof problemSchema>;

/** Distingue un error de la API de un fallo de red o de un fallo del navegador. */
export function isProblem(value: unknown): value is Problem {
  return problemSchema.safeParse(value).success;
}

/* -------------------------------------------------------------------------
 * Salud
 * ---------------------------------------------------------------------- */

export const healthSchema = z.object({
  status: z.literal('ok'),
  uptime: z.number(),
});

export const readinessSchema = z.object({
  status: z.enum(['ready', 'not_ready']),
  checks: z.record(z.string(), z.enum(['ok', 'fail'])),
});

/* -------------------------------------------------------------------------
 * Secciones y mapeo (§7, muro #8)
 * ---------------------------------------------------------------------- */

/**
 * Sección canónica de la organización.
 *
 * Es la respuesta al muro #8 del blueprint: dos periódicos no comparten ni el
 * nombre ni el id de sus categorías. El redactor elige "Política" una vez y
 * cada sitio la traduce a lo que use por dentro.
 */
export const sectionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.iso.datetime(),
});

export type Section = z.infer<typeof sectionSchema>;

export const sectionCreateSchema = z.object({
  name: z.string().min(1, 'La sección necesita un nombre.').max(60),
  /** Si no se envía, se deriva del nombre. */
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Solo minúsculas, números y guiones.')
    .optional(),
});

export type SectionCreate = z.infer<typeof sectionCreateSchema>;

export const sectionUpdateSchema = sectionCreateSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'No hay nada que actualizar.' });

/** Una categoría real de un sitio, tal como la vio el último refresco. */
export const siteCategorySchema = z.object({
  wpTermId: z.number().int(),
  name: z.string(),
  slug: z.string(),
  parentWpId: z.number().int(),
  postCount: z.number().int(),
});

export type SiteCategory = z.infer<typeof siteCategorySchema>;

/**
 * Estado del mapeo de un sitio.
 *
 * `missing` es la información que más importa en la interfaz: son las secciones
 * que ese sitio no sabe dónde poner. Publicar en él con una de esas secciones
 * fallaría, así que conviene enseñarlo antes de que alguien lo intente.
 */
export const siteMappingSchema = z.object({
  siteId: z.uuid(),
  siteName: z.string(),
  categories: z.array(siteCategorySchema),
  categoriesFetchedAt: z.iso.datetime().nullable(),
  mappings: z.array(
    z.object({
      sectionId: z.uuid(),
      wpTermId: z.number().int(),
      wpTermName: z.string().nullable(),
      /** La categoría mapeada ya no existe en el sitio: alguien la borró. */
      stale: z.boolean(),
    }),
  ),
  missingSectionIds: z.array(z.uuid()),
});

export type SiteMapping = z.infer<typeof siteMappingSchema>;

export const sectionMapUpdateSchema = z.object({
  mappings: z
    .array(
      z.object({
        sectionId: z.uuid(),
        /** `null` borra el mapeo de esa sección en este sitio. */
        wpTermId: z.number().int().positive().nullable(),
      }),
    )
    .max(200),
});

export type SectionMapUpdate = z.infer<typeof sectionMapUpdateSchema>;

/* -------------------------------------------------------------------------
 * Grupos de sitios
 * ---------------------------------------------------------------------- */

export const siteGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  siteIds: z.array(z.uuid()),
  createdAt: z.iso.datetime(),
});

export type SiteGroup = z.infer<typeof siteGroupSchema>;

export const siteGroupWriteSchema = z.object({
  name: z.string().min(1, 'El grupo necesita un nombre.').max(60),
  siteIds: z.array(z.uuid()).max(200),
});

export type SiteGroupWrite = z.infer<typeof siteGroupWriteSchema>;

/**
 * Convierte un nombre en slug.
 *
 * Se quitan los acentos antes de filtrar: "Política" tiene que dar "politica",
 * no "pol-tica". Vive en contratos porque el formulario lo usa para previsualizar
 * el slug y el servidor para generarlo, y tienen que coincidir.
 */
export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/* -------------------------------------------------------------------------
 * Artículos
 * ---------------------------------------------------------------------- */

export const articleStatusSchema = z.enum(['draft', 'in_review', 'approved', 'archived']);
export type ArticleStatus = z.infer<typeof articleStatusSchema>;

/**
 * Lo que se guarda al redactar.
 *
 * `contentHtml` lo produce el editor y **el servidor lo vuelve a sanear**: lo
 * que llega del navegador nunca es de fiar, ni siquiera cuando lo generó nuestro
 * propio editor. `contentJson` es el documento del editor, para poder seguir
 * editando sin perder estructura.
 */
export const articleWriteSchema = z.object({
  title: z.string().min(1, 'La noticia necesita un título.').max(300),
  slug: z
    .string()
    .max(200)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Solo minúsculas, números y guiones.')
    .optional(),
  // `nullish` y no `optional`: enviar null es como se BORRA una entradilla que
  // ya estaba puesta. Con solo `optional`, omitir el campo se interpreta como
  // "no lo toques" y no habria forma de vaciarlo.
  excerpt: z.string().max(1000).nullish(),
  contentHtml: z.string().max(500_000).optional(),
  contentJson: z.record(z.string(), z.unknown()).optional(),
  featuredMediaId: z.uuid().nullish(),
  sectionId: z.uuid().nullish(),
  tags: z.array(z.string().min(1).max(60)).max(30).optional(),
  seoTitle: z.string().max(200).nullish(),
  seoDescription: z.string().max(500).nullish(),
  status: articleStatusSchema.optional(),
});

export type ArticleWrite = z.infer<typeof articleWriteSchema>;

export const articleUpdateSchema = articleWriteSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'No hay nada que actualizar.' });

/** Variante de un artículo para un periódico concreto (mitiga el duplicado). */
export const articleOverrideWriteSchema = z.object({
  title: z.string().max(300).nullish(),
  slug: z
    .string()
    .max(200)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Solo minúsculas, números y guiones.')
    .nullish(),
  excerpt: z.string().max(1000).nullish(),
  contentHtml: z.string().max(500_000).nullish(),
  seoTitle: z.string().max(200).nullish(),
  seoDescription: z.string().max(500).nullish(),
  canonicalMode: z.enum(['none', 'origin', 'noindex']).nullish(),
});

export type ArticleOverrideWrite = z.infer<typeof articleOverrideWriteSchema>;

/* -------------------------------------------------------------------------
 * Publicaciones
 * ---------------------------------------------------------------------- */

export const targetStatusSchema = z.enum([
  'pending',
  'preparing',
  'prepared',
  'publishing',
  'published',
  'failed_retrying',
  'failed',
  'canceled',
  'skipped',
]);

export type TargetStatus = z.infer<typeof targetStatusSchema>;

export const publicationStatusSchema = z.enum([
  'scheduled',
  'processing',
  'completed',
  'partial',
  'failed',
  'canceled',
]);

export type PublicationStatus = z.infer<typeof publicationStatusSchema>;

/**
 * Orden de publicación.
 *
 * Los destinos se dan por sitio, por grupo, o mezclando ambos; el servidor los
 * une y quita repetidos. Que se pueda decir "grupo Red Norte" es justo el punto
 * del producto: nadie quiere marcar veinte casillas cada mañana.
 *
 * `scheduledAt` va en UTC y `tz` es la zona en la que la persona lo eligió. Se
 * guardan las dos (§17.11): con solo el instante UTC no se puede volver a
 * mostrar "mañana a las 8:00" ni saber si el cambio de hora lo movió.
 */
export const publicationCreateSchema = z
  .object({
    articleId: z.uuid(),
    siteIds: z.array(z.uuid()).max(500).optional(),
    groupIds: z.array(z.uuid()).max(50).optional(),
    mode: z.enum(['now', 'scheduled']).default('now'),
    scheduledAt: z.iso.datetime({ offset: true }).optional(),
    tz: z.string().max(64).optional(),
  })
  .refine((value) => (value.siteIds?.length ?? 0) + (value.groupIds?.length ?? 0) > 0, {
    message: 'Elige al menos un sitio o un grupo.',
    path: ['siteIds'],
  })
  .refine((value) => value.mode !== 'scheduled' || Boolean(value.scheduledAt), {
    message: 'Una publicación programada necesita fecha y hora.',
    path: ['scheduledAt'],
  });

export type PublicationCreate = z.infer<typeof publicationCreateSchema>;

/** Estado de un destino, tal como lo enseña la pantalla de seguimiento. */
export const publicationTargetViewSchema = z.object({
  id: z.uuid(),
  siteId: z.uuid(),
  siteName: z.string(),
  status: targetStatusSchema,
  remoteUrl: z.string().nullable(),
  attemptCount: z.number().int(),
  nextAttemptAt: z.iso.datetime().nullable(),
  publishedAt: z.iso.datetime().nullable(),
  lastErrorKind: z.string().nullable(),
  lastErrorMessage: z.string().nullable(),
  lastErrorHint: z.string().nullable(),
});

export type PublicationTargetView = z.infer<typeof publicationTargetViewSchema>;

export const publicationViewSchema = z.object({
  id: z.uuid(),
  articleId: z.uuid(),
  articleTitle: z.string(),
  mode: z.enum(['now', 'scheduled']),
  status: publicationStatusSchema,
  scheduledAt: z.iso.datetime().nullable(),
  tzDisplay: z.string().nullable(),
  createdAt: z.iso.datetime(),
  targets: z.array(publicationTargetViewSchema),
});

export type PublicationView = z.infer<typeof publicationViewSchema>;

/* -------------------------------------------------------------------------
 * Sugerencia de mapeo de secciones
 * ---------------------------------------------------------------------- */

/**
 * Propone qué categoría de un sitio corresponde a cada sección.
 *
 * Existe porque en la práctica los periódicos suelen llamar igual a lo mismo:
 * Nacionales, Deportes, Internacionales. Con veinte sitios y cinco secciones son
 * cien desplegables que elegir a mano, casi todos con la respuesta evidente.
 *
 * **Propone, no decide.** La sugerencia se enseña preseleccionada y hay que
 * guardarla; nunca se aplica sola. La razón no es prudencia genérica: un mapeo
 * equivocado publica la noticia en la sección que no es, en el periódico de otro,
 * y eso no se deshace desde aquí.
 *
 * Por eso solo se sugiere con **coincidencia fuerte**, y se dice de qué tipo:
 *
 *   exact     el mismo nombre una vez normalizado ("Deportes" / "deportes")
 *   plural    solo se diferencian en el plural ("Nacional" / "Nacionales")
 *
 * Lo que NO se hace es parecido aproximado. "Política" y "Policiales" comparten
 * principio y no tienen nada que ver; sugerir eso sería peor que no sugerir
 * nada, porque alguien lo aceptaría sin mirar.
 */
export type MatchKind = 'exact' | 'plural';

export interface SectionSuggestion {
  sectionId: string;
  wpTermId: number;
  /** El nombre de la categoría en el sitio, para poder enseñarlo. */
  categoryName: string;
  match: MatchKind;
}

export interface SuggestInput {
  sections: Array<{ id: string; name: string; slug: string }>;
  categories: Array<{ wpTermId: number; name: string; slug: string }>;
  /** Secciones que ya tienen mapeo. No se tocan. */
  alreadyMapped?: ReadonlySet<string>;
}

/**
 * Normaliza para comparar: sin acentos, sin mayúsculas, sin signos.
 *
 * Se quitan los acentos antes de comparar porque "Política" y "Politica"
 * conviven en la misma red de periódicos más a menudo de lo que parece.
 */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Quita el plural español más común para comparar.
 *
 * Deliberadamente tonto: solo la `-s` y la `-es` finales. Una lematización de
 * verdad acertaría más casos y fallaría de formas impredecibles, y aquí un
 * acierto de más no compensa un fallo silencioso.
 */
function singular(value: string): string {
  if (value.endsWith('es') && value.length > 4) {
    return value.slice(0, -2);
  }
  if (value.endsWith('s') && value.length > 3) {
    return value.slice(0, -1);
  }
  return value;
}

export function suggestSectionMapping({
  sections,
  categories,
  alreadyMapped,
}: SuggestInput): SectionSuggestion[] {
  const suggestions: SectionSuggestion[] = [];

  // Una categoría no puede quedar sugerida para dos secciones: si "Deportes" ya
  // se propuso para la sección Deportes, no puede proponerse también para
  // Deporte. Mandaría la primera, y cuál es la primera dependería del orden.
  const taken = new Set<number>();

  for (const section of sections) {
    if (alreadyMapped?.has(section.id)) {
      continue;
    }

    const target = normalize(section.name);
    const targetSlug = normalize(section.slug);

    const exact = categories.find(
      (category) =>
        !taken.has(category.wpTermId) &&
        (normalize(category.name) === target || normalize(category.slug) === targetSlug),
    );

    if (exact) {
      taken.add(exact.wpTermId);
      suggestions.push({
        sectionId: section.id,
        wpTermId: exact.wpTermId,
        categoryName: exact.name,
        match: 'exact',
      });
      continue;
    }

    const byPlural = categories.find(
      (category) => !taken.has(category.wpTermId) && singular(normalize(category.name)) === singular(target),
    );

    if (byPlural) {
      taken.add(byPlural.wpTermId);
      suggestions.push({
        sectionId: section.id,
        wpTermId: byPlural.wpTermId,
        categoryName: byPlural.name,
        match: 'plural',
      });
    }
  }

  return suggestions;
}

/* -------------------------------------------------------------------------
 * Administración de la plataforma: clientes y sus límites
 * ---------------------------------------------------------------------- */

/**
 * Topes razonables para los propios límites.
 *
 * No son planes comerciales: son la barrera contra un dedo de más en el
 * formulario. Nadie quiere descubrir que dio de alta un cliente con 10.000
 * periódicos porque se le fue un cero.
 */
const siteLimitSchema = z.number().int().min(1, 'Mínimo 1 periódico.').max(500, 'Máximo 500 periódicos.');
const memberLimitSchema = z.number().int().min(1, 'Mínimo 1 miembro.').max(200, 'Máximo 200 miembros.');

/** Alta de un cliente: su organización y la invitación a quien será propietario. */
export const platformClientCreateSchema = z.object({
  name: z.string().trim().min(2, 'El nombre es demasiado corto.').max(80),
  ownerEmail: z.email('Ese correo no parece válido.').max(254),
  maxSites: siteLimitSchema.default(10),
  maxMembers: memberLimitSchema.default(5),
});

export type PlatformClientCreate = z.infer<typeof platformClientCreateSchema>;

export const platformClientUpdateSchema = z
  .object({
    maxSites: siteLimitSchema,
    maxMembers: memberLimitSchema,
    suspended: z.boolean(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'No hay nada que actualizar.' });

export type PlatformClientUpdate = z.infer<typeof platformClientUpdateSchema>;

/** Un cliente tal como lo ve quien administra la plataforma. */
export const platformClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  owners: z.array(z.object({ name: z.string(), email: z.string() })),
  /** Invitación a propietario enviada y aún sin aceptar, si la hay. */
  pendingOwnerInvitation: z
    .object({ email: z.string(), expiresAt: z.iso.datetime(), expired: z.boolean() })
    .nullable(),
  limits: z.object({ maxSites: z.number().int(), maxMembers: z.number().int() }),
  usage: z.object({
    sites: z.number().int(),
    members: z.number().int(),
    pendingInvitations: z.number().int(),
    publicationsLast30Days: z.number().int(),
  }),
  lastPublicationAt: z.iso.datetime().nullable(),
  suspended: z.boolean(),
  paused: z.boolean(),
});

export type PlatformClient = z.infer<typeof platformClientSchema>;

/** Lo que una organización ve de su propio contrato: cuánto tiene y cuánto usa. */
export const orgLimitsSchema = z.object({
  limits: z.object({ maxSites: z.number().int(), maxMembers: z.number().int() }),
  usage: z.object({
    sites: z.number().int(),
    members: z.number().int(),
    pendingInvitations: z.number().int(),
  }),
  /** Suspendida por quien administra la plataforma: no puede publicar. */
  suspended: z.boolean(),
});

export type OrgLimitsView = z.infer<typeof orgLimitsSchema>;
